import { FC, useMemo } from 'react';
import dayjs from 'dayjs';
import { classifyCoverage } from '@leopart/calc';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import useSubjectReadinessPage from '../../hooks/useSubjectReadinessPage';
import HooperHistoryModal, {
  HooperMetric,
} from '../../components/DetailTeamPage/SubjectReadinessTab/HooperHistoryModal';
import LoadingPage from '../../shared/LoadingPage/LoadingPage';
import CustomDatePicker from '../../shared/CustomDatePicker/CustomDatePicker';
import { ShowByRole } from '../../shared/ShowByRole';
import styles from './SubjectReadinessPage.module.scss';

/**
 * Readiness soggettiva — sidebar page.
 * Stile ispirato a TeamReadinessTab (matrix view) ma semplificato:
 * summary bar + filter bar + tableCard con celle colorate.
 */

const HOOPER_COLS: Array<{ key: HooperMetric; label: string }> = [
  { key: 'fatigue', label: 'Fatica' },
  { key: 'sleepQuality', label: 'Sonno (qualità)' },
  { key: 'muscleSoreness', label: 'Dolore muscoli' },
  { key: 'stressLevel', label: 'Stress' },
  { key: 'mood', label: 'Umore' },
];

/** Scala Hooper 1-5: 5 ottimo, 3-4 ok, 1-2 rosso. */
function hooperColor(v: number | null | undefined): {
  bg: string;
  color: string;
} {
  if (v == null) return { bg: 'transparent', color: '#cbd0d3' };
  if (v === 5) return { bg: 'rgba(39, 174, 96, 0.15)', color: '#1a8a5a' };
  if (v >= 3) return { bg: 'rgba(255, 209, 90, 0.2)', color: '#8a5700' };
  return { bg: 'rgba(245, 124, 115, 0.15)', color: '#c72d20' };
}

/** Scala average 1-5 colorata come badge pieno. */
function avgBadgeColor(v: number): string {
  if (v >= 4) return '#27ae60';
  if (v >= 3) return '#FFD15A';
  if (v >= 2) return '#FF9F43';
  return '#f57c73';
}

function getHooper(record: any) {
  return Array.isArray(record?.hooperQuestionnaire)
    ? record.hooperQuestionnaire[0]
    : record?.hooperQuestionnaire;
}

function computeAverage(q: any): number | null {
  if (!q) return null;
  if (q.average != null) return q.average;
  const keys = [
    'fatigue',
    'sleepQuality',
    'muscleSoreness',
    'stressLevel',
    'mood',
  ];
  const values = keys.map((k) => q[k]).filter((v: any) => v != null);
  if (values.length === 0) return null;
  return (
    values.reduce((s: number, v: number) => s + v, 0) / values.length
  );
}

const SubjectReadinessPage: FC = () => {
  const {
    query,
    filteredData,
    searchValue,
    updateSearchValue,
    date,
    setDate,
    selectedCell,
    setSelectedCell,
  } = useSubjectReadinessPage();

  const summary = useMemo(() => {
    const list: any[] = filteredData ?? [];
    const compiled = list.filter((r) => getHooper(r)).length;
    const total = list.length;
    const averages = list
      .map((r) => computeAverage(getHooper(r)))
      .filter((v): v is number => v != null);
    const avgAll =
      averages.length > 0
        ? averages.reduce((s, v) => s + v, 0) / averages.length
        : null;
    return {
      compiled,
      total,
      compilationPct: total > 0 ? (compiled / total) * 100 : 0,
      avgAll,
      missing: total - compiled,
    };
  }, [filteredData]);

  if (query.isPending) return <LoadingPage />;

  const list = (filteredData ?? []) as any[];

  return (
    <>
      <div className={styles.pageWrapper}>
        {/* ─── Header ─────────────────────────────────────────────── */}
        <div className={styles.titleRow}>
          {/* BUG-A11Y-016: heading h1 semantic (era span) */}
          <h1 className={styles.pageTitle}>Readiness soggettiva</h1>
          <span className={styles.pageSubtitle}>
            Questionario Hooper compilato dagli atleti · filtro per giorno
          </span>
        </div>

        {/* ─── Summary bar ─────────────────────────────────────────── */}
        <div className={styles.summaryBar}>
          <div className={styles.summaryBlock}>
            <span className={styles.summaryLabel}>Compilazione</span>
            <div className={styles.summaryCompilation}>
              <span className={styles.summaryValueLg}>{summary.compiled}</span>
              <span className={styles.summaryValueSub}>/ {summary.total}</span>
              <div className={styles.summaryProgressTrack}>
                <div
                  className={styles.summaryProgressFill}
                  style={{
                    width: `${summary.compilationPct}%`,
                    background:
                      classifyCoverage(summary.compilationPct) === 'good'
                        ? '#27ae60'
                        : classifyCoverage(summary.compilationPct) === 'warn'
                          ? '#FFD15A'
                          : '#f57c73',
                  }}
                />
              </div>
            </div>
          </div>

          <div className={styles.summaryDivider} />

          <div className={styles.summaryBlock}>
            <span className={styles.summaryLabel}>Media Hooper</span>
            <span
              className={styles.summaryValue}
              style={{
                color:
                  summary.avgAll != null
                    ? avgBadgeColor(summary.avgAll)
                    : '#b5b5b5',
              }}
            >
              {summary.avgAll != null ? summary.avgAll.toFixed(2) : '—'}
            </span>
          </div>

          <div className={styles.summaryDivider} />

          <div className={styles.summaryBlock}>
            <span className={styles.summaryLabel}>Data</span>
            <span className={styles.summaryValue}>
              {date ? dayjs(date).format('D MMM YYYY') : '—'}
            </span>
          </div>

          <div className={styles.summaryBadges}>
            {summary.missing > 0 ? (
              <span className={styles.summaryBadgeWarn}>
                {summary.missing} non compilati
              </span>
            ) : summary.total > 0 ? (
              <span className={styles.summaryBadgeOk}>Tutti compilati</span>
            ) : null}
          </div>
        </div>

        {/* ─── Filter bar ──────────────────────────────────────────── */}
        <div className={styles.filterBar}>
          <div className={styles.filterLeft}>
            <div className={styles.searchWrapper}>
              <MagnifyingGlassIcon size={14} className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Cerca atleta…"
                value={searchValue}
                onChange={updateSearchValue}
              />
            </div>
          </div>

          <ShowByRole roles={['isAdmin', 'isDirector']}>
            <div className={styles.dateNavGroup}>
              <button
                type="button"
                className={styles.dateArrow}
                onClick={() => setDate(dayjs(date).subtract(1, 'd').toDate())}
                title="Giorno precedente"
              >
                <ArrowLeftIcon size={14} />
              </button>
              <CustomDatePicker date={date} onChange={setDate} />
              <button
                type="button"
                className={styles.dateArrow}
                onClick={() => setDate(dayjs(date).add(1, 'd').toDate())}
                title="Giorno successivo"
              >
                <ArrowRightIcon size={14} />
              </button>
            </div>
          </ShowByRole>
        </div>

        {/* ─── Table card ─────────────────────────────────────────── */}
        <div className={styles.tableCard}>
          <div className={styles.tableScrollWrapper}>
            <table className={styles.table}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.thName}>Atleta</th>
                  {HOOPER_COLS.map((c) => (
                    <th key={c.key} className={styles.th}>
                      {c.label}
                    </th>
                  ))}
                  <th className={styles.th}>Media</th>
                  <th className={styles.th}>Sonno (h)</th>
                  <th className={styles.th}>Prontezza</th>
                  <th className={styles.th}>Note</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 && !query.isFetching && (
                  <tr>
                    <td
                      colSpan={HOOPER_COLS.length + 5}
                      className={styles.emptyState}
                    >
                      Nessun atleta in questo periodo
                    </td>
                  </tr>
                )}

                {query.isFetching
                  ? Array.from({ length: 6 }).map((_, i) => (
                      <tr key={`sk-${i}`} className={styles.skeletonRow}>
                        <td className={styles.tdName}>
                          <div className={styles.skeletonName} />
                        </td>
                        {Array.from({
                          length: HOOPER_COLS.length + 4,
                        }).map((__, j) => (
                          <td key={j}>
                            <div className={styles.skeletonCell} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : list.map((record: any) => {
                      const q = getHooper(record);
                      const avg = computeAverage(q);
                      return (
                        <tr key={record._id} className={styles.tableRow}>
                          <td className={styles.tdName}>
                            {record.lastName} {record.firstName}
                          </td>
                          {HOOPER_COLS.map((c) => {
                            const v = q?.[c.key];
                            const colors = hooperColor(v);
                            return (
                              <td
                                key={c.key}
                                className={`${styles.td} ${v != null ? styles.tdClickable : ''}`}
                                onClick={() =>
                                  v != null &&
                                  setSelectedCell({
                                    athleteId: String(record._id),
                                    athleteName: `${record.firstName} ${record.lastName}`,
                                    metric: c.key,
                                  })
                                }
                              >
                                {v != null ? (
                                  <span
                                    className={styles.metricValue}
                                    style={{
                                      background: colors.bg,
                                      color: colors.color,
                                    }}
                                  >
                                    {v}
                                  </span>
                                ) : (
                                  <span className={styles.metricDash}>—</span>
                                )}
                              </td>
                            );
                          })}

                          {/* Media */}
                          <td
                            className={`${styles.td} ${avg != null ? styles.tdClickable : ''}`}
                            onClick={() =>
                              avg != null &&
                              setSelectedCell({
                                athleteId: String(record._id),
                                athleteName: `${record.firstName} ${record.lastName}`,
                                metric: 'average',
                              })
                            }
                          >
                            {avg != null ? (
                              <span
                                className={styles.avgBadge}
                                style={{ background: avgBadgeColor(avg) }}
                              >
                                {avg.toFixed(1)}
                              </span>
                            ) : (
                              <span className={styles.metricDash}>—</span>
                            )}
                          </td>

                          {/* Sonno h */}
                          <td className={styles.td}>
                            {q?.sleepHours != null ? (
                              <span>{q.sleepHours}h</span>
                            ) : (
                              <span className={styles.metricDash}>—</span>
                            )}
                          </td>

                          {/* Prontezza */}
                          <td className={styles.td}>
                            {q?.readinessToTrain != null ? (
                              <span>{q.readinessToTrain}/10</span>
                            ) : (
                              <span className={styles.metricDash}>—</span>
                            )}
                          </td>

                          {/* Note */}
                          <td className={styles.td}>
                            {q?.notes ? (
                              <span
                                className={styles.notesIcon}
                                title={q.notes}
                              >
                                📝
                              </span>
                            ) : (
                              <span className={styles.metricDash}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <HooperHistoryModal
        isOpen={!!selectedCell}
        onClose={() => setSelectedCell(null)}
        athleteId={selectedCell?.athleteId ?? null}
        athleteName={selectedCell?.athleteName ?? null}
        metric={selectedCell?.metric ?? null}
      />
    </>
  );
};

export default SubjectReadinessPage;
